import { useTranslation } from "@sagot/i18n";
import { Button, EmptyState, ErrorState, Icon, Input, Select } from "@sagot/ui";
import type { UseQueryResult } from "@tanstack/react-query";
import { useState } from "react";
import { distinctInterests } from "./lead-logic";
import type { Lead, Stage } from "./lead-types";
import { LeadsBoard } from "./leads-board";
import { LeadsTable } from "./leads-table";
import { LeadsViewSwitch } from "./leads-view-switch";
import { StageTabs } from "./stage-tabs";
import { ALL_STAGES, type useLeadsRoute } from "./use-leads-route";
import { useLeadBoard, useLeadList } from "./use-leads";

const PANEL_ID = "leads-panel";

interface LeadsPanelProps {
  route: ReturnType<typeof useLeadsRoute>;
  stages: UseQueryResult<Stage[]>;
  canEdit: boolean;
  overrides: Readonly<Record<string, string>>;
  onMove: (lead: Lead, stage: Stage) => void;
}

function useInterestOptions(leads: readonly Lead[]) {
  const [known, setKnown] = useState<string[]>([]);
  const fresh = distinctInterests(leads).filter((i) => !known.includes(i));
  if (fresh.length > 0) setKnown([...known, ...fresh].sort((a, b) => a.localeCompare(b)));
  return known;
}

/** Table card (stage tabs + search + interest filter) or the pipeline board. */
export function LeadsPanel({ route, stages, canEdit, overrides, onMove }: LeadsPanelProps) {
  const { t } = useTranslation();
  const isBoard = route.view === "board";
  const list = useLeadList(route.filters, !isBoard);
  const board = useLeadBoard(route.filters.q, isBoard);
  const leads = (list.data?.pages ?? []).flatMap((p) => p.items).map((l) => (overrides[l.id] ? { ...l, stage: { ...l.stage, id: overrides[l.id] ?? l.stage.id } } : l));
  const interests = useInterestOptions(leads);
  const total = list.data?.pages[0]?.total ?? 0;
  const stageList = stages.data ?? [];
  const allCount = stageList.reduce((n, s) => n + s.lead_count, 0);
  const filtered = route.filters.stageId !== null || route.filters.q !== "" || route.filters.interest !== null;
  const failed = isBoard ? board.isError : list.isError || stages.isError;

  const empty = (
    <EmptyState
      icon="user-check"
      headingLevel={3}
      title={t("engage.leads.emptyTitle")}
      description={filtered ? t("engage.leads.emptyFiltered") : t("engage.leads.emptyBody")}
      action={
        filtered ? (
          <Button variant="secondary" onClick={route.clearFilters}>
            {t("engage.leads.clearFilters")}
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <section className="card overflow-hidden" aria-label={t("engage.leads.title")}>
      <div className="flex flex-col gap-3 border-b border-line p-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <LeadsViewSwitch value={route.view} onValueChange={route.setView} />
          {isBoard ? null : (
            <StageTabs
              label={t("engage.leads.stageFilter")}
              controls={PANEL_ID}
              value={route.stage}
              onChange={route.setStage}
              tabs={[{ value: ALL_STAGES, label: t("engage.leads.all"), count: stages.data ? allCount : null }, ...stageList.map((s) => ({ value: s.id, label: s.name, count: s.lead_count }))]}
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-full lg:w-64">
            <Icon name="search" className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle" />
            <Input className="h-9 pl-9" type="search" placeholder={t("engage.leads.search")} aria-label={t("engage.leads.search")} value={route.query} onChange={(e) => { route.setQuery(e.target.value); }} />
          </div>
          {isBoard ? null : (
            <Select className="h-9 w-44" aria-label={t("engage.leads.interest")} value={route.filters.interest ?? ""} onChange={(e) => { route.setInterest(e.target.value || null); }}>
              <option value="">{t("engage.leads.allInterests")}</option>
              {interests.map((interest) => (
                <option key={interest} value={interest}>
                  {interest}
                </option>
              ))}
            </Select>
          )}
        </div>
      </div>
      {failed ? (
        <ErrorState title={t("engage.leads.loadError")} retryLabel={t("common.retry")} onRetry={() => void (isBoard ? board.refetch() : Promise.all([list.refetch(), stages.refetch()]))} />
      ) : isBoard ? (
        <LeadsBoard id={PANEL_ID} columns={board.data} isPending={board.isPending} canEdit={canEdit} overrides={overrides} leadHref={route.leadHref} onMove={onMove} />
      ) : (
        <>
          <LeadsTable
            id={PANEL_ID}
            leads={leads}
            stages={stageList}
            isPending={list.isPending || stages.isPending}
            canEdit={canEdit}
            leadHref={route.leadHref}
            empty={empty}
            onStageChange={(lead, stageId) => {
              const stage = stageList.find((s) => s.id === stageId);
              if (stage) onMove(lead, stage);
            }}
          />
          <div className="flex items-center justify-between border-t border-line px-4 py-3 text-xs text-fg-subtle">
            <span aria-live="polite">{list.data ? t("engage.leads.showing", { shown: leads.length, total }) : ""}</span>
            {list.hasNextPage ? (
              <Button variant="secondary" size="sm" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
                {t("engage.loadMore")}
              </Button>
            ) : null}
          </div>
        </>
      )}
    </section>
  );
}
